import React, { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  InputAdornment,
} from "@mui/material";
import { LoadingPlanSteps } from "../../CreateLoadingPlanPage";
import { useLoadingPlan } from "../../../../contexts/LoadingPlanContext";
import "./Step2.scss";

interface Step2Props {
  setCurrentStep: (step: LoadingPlanSteps) => void;
}

export interface Step2Ref {
  validate: () => boolean;
}

const Step2 = forwardRef<Step2Ref, Step2Props>((_props, ref) => {
  const { currentLoadingPlan, updateCurrentLoadingPlan } = useLoadingPlan();

  const [trailerLength, setTrailerLength] = useState<number | string>(currentLoadingPlan.trailerLength);
  const [maxWidth, setMaxWidth] = useState<number | string>(currentLoadingPlan.trailerMaxWidth);
  const [maxWeight, setMaxWeight] = useState<number | string>(currentLoadingPlan.trailerMaxWeight);
  const [maxHeight, setMaxHeight] = useState<number | string>(currentLoadingPlan.trailerMaxHeight);

  const [errors, setErrors] = useState({
    trailerLength: "",
    maxWidth: "",
    maxWeight: "",
    maxHeight: "",
  });

  useImperativeHandle(ref, () => ({
    validate() {
      const numericRegex = /^[0-9]+\.?[0-9]*$/;
      const newErrors = {
        trailerLength: String(trailerLength) === "" || !numericRegex.test(String(trailerLength)) ? "Trailer Length is required" : "",
        maxWidth: String(maxWidth) === "" || !numericRegex.test(String(maxWidth)) ? "Max Width is required" : "",
        maxWeight: String(maxWeight) === "" || !numericRegex.test(String(maxWeight)) ? "Max Weight Capacity is required" : "",
        maxHeight: String(maxHeight) === "" || !numericRegex.test(String(maxHeight)) ? "Max Height is required" : "",
      };
      setErrors(newErrors);
      return Object.values(newErrors).every((e) => e === "");
    },
  }));

  // Validation helper: only allow numbers with optional decimal point
  const validateNumericInput = (value: string): boolean => {
    // Allow empty string
    if (value === "") return true;
    // Allow only numbers and one decimal point
    const numericRegex = /^[0-9]*\.?[0-9]*$/;
    return numericRegex.test(value);
  };

  // Initialize from current loading plan on mount
  useEffect(() => {
    if (currentLoadingPlan.trailerLength) setTrailerLength(currentLoadingPlan.trailerLength);
    if (currentLoadingPlan.trailerMaxWidth) setMaxWidth(currentLoadingPlan.trailerMaxWidth);
    if (currentLoadingPlan.trailerMaxWeight) setMaxWeight(currentLoadingPlan.trailerMaxWeight);
    if (currentLoadingPlan.trailerMaxHeight) setMaxHeight(currentLoadingPlan.trailerMaxHeight);
  }, [currentLoadingPlan.trailerLength, currentLoadingPlan.trailerMaxWidth, currentLoadingPlan.trailerMaxWeight, currentLoadingPlan.trailerMaxHeight]);

  // Update current loading plan when values change
  const handleTrailerLengthChange = (value: string) => {
    // Validate: only numbers with optional dot, max 4 characters
    if (validateNumericInput(value) && value.length <= 4) {
      setTrailerLength(value);
      setErrors((prev) => ({ ...prev, trailerLength: "" }));
      updateCurrentLoadingPlan({
        trailerLength: value === "" || isNaN(Number(value)) ? undefined : Number(value)
      });
    }
  };

  const handleMaxWidthChange = (value: string) => {
    // Validate: only numbers with optional dot, max 3 characters
    if (validateNumericInput(value) && value.length <= 3) {
      setMaxWidth(value);
      setErrors((prev) => ({ ...prev, maxWidth: "" }));
      updateCurrentLoadingPlan({
        trailerMaxWidth: value === "" || isNaN(Number(value)) ? undefined : Number(value)
      });
    }
  };

  const handleMaxWeightChange = (value: string) => {
    // Validate: only numbers with optional dot, max 7 characters
    if (validateNumericInput(value) && value.length <= 7) {
      setMaxWeight(value);
      setErrors((prev) => ({ ...prev, maxWeight: "" }));
      updateCurrentLoadingPlan({
        trailerMaxWeight: value === "" || isNaN(Number(value)) ? undefined : Number(value)
      });
    }
  };

  const handleMaxHeightChange = (value: string) => {
    // Validate: only numbers with optional dot, max 3 characters
    if (validateNumericInput(value) && value.length <= 3) {
      setMaxHeight(value);
      setErrors((prev) => ({ ...prev, maxHeight: "" }));
      updateCurrentLoadingPlan({
        trailerMaxHeight: value === "" || isNaN(Number(value)) ? undefined : Number(value)
      });
    }
  };

  return (
    <div className="step2">
      <Paper className="form-card">
        <Typography variant="subtitle1" fontWeight={600} mb={1}>
          Step 2: Configure Trailer Parameters
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Set dimensions and constraints for the loading plan
        </Typography>

        {/* Form Fields */}
        <Box className="form-grid">
          <TextField
            label="Trailer Length"
            value={trailerLength}
            onChange={(e) => handleTrailerLengthChange(e.target.value)}
            InputProps={{ endAdornment: <InputAdornment position="end">ft</InputAdornment> }}
            inputProps={{ maxLength: 4 }}
            error={!!errors.trailerLength}
            helperText={errors.trailerLength}
            fullWidth
          />

          <TextField
            label="Max Width"
            value={maxWidth}
            onChange={(e) => handleMaxWidthChange(e.target.value)}
            InputProps={{ endAdornment: <InputAdornment position="end">ft</InputAdornment> }}
            inputProps={{ maxLength: 3 }}
            error={!!errors.maxWidth}
            helperText={errors.maxWidth}
            fullWidth
          />

          <TextField
            label="Max Weight Capacity"
            value={maxWeight}
            onChange={(e) => handleMaxWeightChange(e.target.value)}
            InputProps={{ endAdornment: <InputAdornment position="end">lbs</InputAdornment> }}
            inputProps={{ maxLength: 7 }}
            error={!!errors.maxWeight}
            helperText={errors.maxWeight}
            fullWidth
          />

          <TextField
            label="Max Height (including dunnage)"
            value={maxHeight}
            onChange={(e) => handleMaxHeightChange(e.target.value)}
            InputProps={{ endAdornment: <InputAdornment position="end">ft</InputAdornment> }}
            inputProps={{ maxLength: 3 }}
            error={!!errors.maxHeight}
            helperText={errors.maxHeight}
            fullWidth
          />
        </Box>
      </Paper>
    </div>
  );
});

export { Step2 };
